import { createSlice, createAsyncThunk, PayloadAction, current } from '@reduxjs/toolkit';
import type { ProjectData, ProjectMetaData, GetProjectsParams, CreateProjectParams, ProjectsResponse, DeleteProjectResponse } from '../../types/project.types';
import apiClient from '../../api/client';
import { ApiUrls } from '../../api/ApiUrls';

export interface ProjectConversation {
  id: string;
  name?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  mode?: string;
  [key: string]: any;
}

export interface ProjectSliceState {
  projects: ProjectData[];
  projectMetaData: ProjectMetaData | null;
  metadataConversationId: string | null;
  conversations: ProjectConversation[];
  attachments: any[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  isFetchingSingle: boolean;
  isFetchingConversations: boolean;
  isFetchingAttachments: boolean;
  isUploadingFiles: boolean;
  isDeletingAttachment: boolean;
  error: string | null;
  conversationsError: string | null;
  attachmentsError: string | null;
}

const initialState: ProjectSliceState = {
  projects: [],
  projectMetaData: null,
  metadataConversationId: null,
  conversations: [],
  attachments: [],
  isLoading: false,
  isCreating: false,
  isDeleting: false,
  isUpdating: false,
  isFetchingSingle: false,
  isFetchingConversations: false,
  isFetchingAttachments: false,
  isUploadingFiles: false,
  isDeletingAttachment: false,
  error: null,
  conversationsError: null,
  attachmentsError: null,
};

// Async thunk for fetching projects
export const getUserProjectsWithPagination = createAsyncThunk<
  ProjectsResponse,
  GetProjectsParams
>(
  'project/getWithPagination',
  async (params, { rejectWithValue }) => {
    try {
      const url = ApiUrls.getUserProjectsWithPagination(params.limit, params.page);
      const { data } = await apiClient.get(url);
      
      // API returns array directly or object with data property
      const projects = Array.isArray(data) ? data : (data.data || []);
      
      return {
        data: projects,
        total: data.total || projects.length,
        page: params.page,
        limit: params.limit,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch projects');
    }
  }
);

// Async thunk for creating a project
export const createProject = createAsyncThunk<
  { project_id: string; data: ProjectData },
  CreateProjectParams
>(
  'project/create',
  async (params, { rejectWithValue }) => {
    try {
      const requestBody = {
        name: params.name,
        description: params.description,
        tmp_conversation_id: params.tmp_conversation_id,
        ideoz_role: params.ideoz_role,
        main_persona: params.main_persona,
        industry: params.industry,
      };
      const { data } = await apiClient.post(ApiUrls.createProject, requestBody);
      
      // API returns { project_id, data: projectData } or just projectData
      const projectId = data.project_id || data.data?.id || data.id;
      const projectData = data.data || data;
      
      return {
        project_id: projectId,
        data: projectData,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create project');
    }
  }
);

// Async thunk for deleting a project
export const deleteProject = createAsyncThunk<
  DeleteProjectResponse,
  string
>(
  'project/delete',
  async (projectId, { rejectWithValue }) => {
    try {
      await apiClient.delete(ApiUrls.deleteProject(projectId));
      return {
        success: true,
        projectId,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete project');
    }
  }
);

// Async thunk for fetching a single project
export const getProject = createAsyncThunk<
  ProjectData,
  string
>(
  'project/getOne',
  async (projectId, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get(ApiUrls.getProject(projectId));
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch project');
    }
  }
);

// Async thunk for updating a project
export const updateProject = createAsyncThunk<
  ProjectData,
  { projectId: string; projectData: any }
>(
  'project/update',
  async ({ projectId, projectData }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.patch(ApiUrls.updateProject(projectId), projectData);
      return data?.data || data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update project');
    }
  }
);

// Async thunk for fetching project conversations
export const getProjectConversations = createAsyncThunk<
  ProjectConversation[],
  { projectId: string; mode?: 'uncover' | 'prototyper' }
>(
  'project/getConversations',
  async ({ projectId, mode }, { rejectWithValue }) => {
    try {
      const url = ApiUrls.projectConversations(projectId);
      const params = mode ? { mode } : {};
      const { data } = await apiClient.get(url, { params });
      
      // API might return array directly or object with data property
      const conversations = Array.isArray(data) ? data : (data.data || data.conversations || []);
      
      return conversations;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch conversations');
    }
  }
);

// Async thunk for fetching project attachments
export const getProjectAttachments = createAsyncThunk<
  any[],
  string
>(
  'project/getAttachments',
  async (projectId, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get(ApiUrls.getProjectAttachments(projectId));
      const attachments = Array.isArray(data) ? data : (data.data || data.attachments || []);
      return attachments;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch attachments');
    }
  }
);

// Async thunk for uploading project files
export const uploadProjectFiles = createAsyncThunk<
  any[],
  { projectId: string; formData: FormData }
>(
  'project/uploadFiles',
  async ({ projectId, formData }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(ApiUrls.uploadProjectFiles(projectId), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Handle different response formats
      // Single file upload returns: { data: { attachment_id, file_size, ... } }
      // Multiple file upload returns: { data: [...] } or { files: [...] }
      let uploadedFiles;
      if (Array.isArray(data)) {
        uploadedFiles = data;
      } else if (data.data) {
        // If data.data exists and is an object (not array), wrap it in array
        uploadedFiles = Array.isArray(data.data) ? data.data : [data.data];
      } else if (data.files) {
        uploadedFiles = data.files;
      } else {
        // Single file object returned directly
        uploadedFiles = [data];
      }
      
      return uploadedFiles;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to upload files');
    }
  }
);

// Async thunk for deleting project attachment
export const deleteProjectAttachment = createAsyncThunk<
  string,
  string
>(
  'project/deleteAttachment',
  async (fileId, { rejectWithValue }) => {
    try {
      await apiClient.delete(ApiUrls.deleteProjectAttachment(fileId));
      return fileId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete attachment');
    }
  }
);

// Async thunk for updating project mode (state only, no API call)
export const updateProjectMode = createAsyncThunk<
  { projectId: string; mode: 'uncover' | 'prototyper' },
  { projectId: string; mode: 'uncover' | 'prototyper' }
>(
  'project/updateMode',
  async ({ projectId, mode }) => {
    // Update state only, no API call
    return { projectId, mode };
  }
);

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjectMetaData: (state, action: PayloadAction<ProjectMetaData | null>) => {
      state.projectMetaData = action.payload;
    },
    setMetadataConversationId: (state, action: PayloadAction<string | null>) => {
      state.metadataConversationId = action.payload;
    },
    setProjectMode: (state, action: PayloadAction<{ projectId: string; mode: 'uncover' | 'prototyper' }>) => {
      const project = state.projects.find((p: ProjectData) => p.id === action.payload.projectId);
      if (project) {
        project.mode = action.payload.mode;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    clearConversations: (state) => {
      state.conversations = [];
      state.conversationsError = null;
    },
    clearAttachments: (state) => {
      state.attachments = [];
      state.attachmentsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get projects
      .addCase(getUserProjectsWithPagination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProjectsWithPagination.fulfilled, (state, action) => {
        state.projects = action.payload.data.map((project: ProjectData) => ({
          ...project,
          mode: project.mode || 'uncover',
        }));
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUserProjectsWithPagination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create project
      .addCase(createProject.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        const newProject = {
          ...action.payload.data,
          mode: action.payload.data.mode || 'uncover',
        };
        state.projects.unshift({...newProject, id: action.payload.project_id || newProject.id });
        state.isCreating = false;
        state.error = null;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Delete project
      .addCase(deleteProject.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter(
          (p: ProjectData) => p.id !== action.payload.projectId
        );
        state.isDeleting = false;
        state.error = null;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Get single project
      .addCase(getProject.pending, (state) => {
        state.isFetchingSingle = true;
        state.error = null;
      })
      .addCase(getProject.fulfilled, (state, action) => {
        // Add or update project in the list
        const projectWithMode = {
          ...action.payload,
          mode: action.payload.mode || 'uncover',
        };
        const existingIndex = state.projects.findIndex((p: ProjectData) => p.id === action.payload.id);
        if (existingIndex >= 0) {
          state.projects[existingIndex] = projectWithMode;
        } else {
          state.projects.unshift(projectWithMode);
        }
        state.isFetchingSingle = false;
        state.error = null;
      })
      .addCase(getProject.rejected, (state, action) => {
        state.isFetchingSingle = false;
        state.error = action.payload as string;
      })
      // Update project
      .addCase(updateProject.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        // Update project in the list
        const index = state.projects.findIndex((p: ProjectData) => p.id === action.payload.id);
        if (index >= 0) {
          state.projects[index] = { ...state.projects[index], ...action.payload, hasUpdateDetails: true};
        }
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Get project conversations
      .addCase(getProjectConversations.pending, (state) => {
        state.isFetchingConversations = true;
        state.conversationsError = null;
      })
      .addCase(getProjectConversations.fulfilled, (state, action) => {
        state.conversations = action.payload;
        state.isFetchingConversations = false;
        state.conversationsError = null;
      })
      .addCase(getProjectConversations.rejected, (state, action) => {
        state.isFetchingConversations = false;
        state.conversationsError = action.payload as string;
      })
      // Get project attachments
      .addCase(getProjectAttachments.pending, (state) => {
        state.isFetchingAttachments = true;
        state.attachmentsError = null;
      })
      .addCase(getProjectAttachments.fulfilled, (state, action) => {
        state.attachments = action.payload;
        state.isFetchingAttachments = false;
        state.attachmentsError = null;
      })
      .addCase(getProjectAttachments.rejected, (state, action) => {
        state.isFetchingAttachments = false;
        state.attachmentsError = action.payload as string;
      })
      // Upload project files
      .addCase(uploadProjectFiles.pending, (state) => {
        state.isUploadingFiles = true;
        state.attachmentsError = null;
      })
      .addCase(uploadProjectFiles.fulfilled, (state, action) => {
        // Add new files to attachments array
        // Map attachment_id to id for consistency
        const projectId = action.meta.arg.projectId;
        const newFiles = action.payload.map((file: any) => ({
          ...file,
          id: file.attachment_id || file.id,
          fileName: file.original_filename || file.fileName || file.file_name,
        }));
        const index = state.projects.findIndex((p: ProjectData) => p.id === projectId);
        if (index >= 0) {
          state.projects[index] = { ...state.projects[index], hasAddAttachment: true};
        }
        state.attachments = [...state.attachments, ...newFiles];
        state.isUploadingFiles = false;
        state.attachmentsError = null;
      })
      .addCase(uploadProjectFiles.rejected, (state, action) => {
        state.isUploadingFiles = false;
        state.attachmentsError = action.payload as string;
      })
      // Delete project attachment
      .addCase(deleteProjectAttachment.pending, (state) => {
        state.isDeletingAttachment = true;
        state.attachmentsError = null;
      })
      .addCase(deleteProjectAttachment.fulfilled, (state, action) => {
        // Remove deleted file from attachments array
        // Match by id or attachment_id
        state.attachments = state.attachments.filter(
          (att: any) => att.id !== action.payload && att.attachment_id !== action.payload
        );
        state.isDeletingAttachment = false;
        state.attachmentsError = null;
      })
      .addCase(deleteProjectAttachment.rejected, (state, action) => {
        state.isDeletingAttachment = false;
        state.attachmentsError = action.payload as string;
      })
      // Update project mode (state only, no API call)
      .addCase(updateProjectMode.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProjectMode.fulfilled, (state, action) => {
        const project = state.projects.find((p: ProjectData) => p.id === action.payload.projectId);
        if (project) {
          project.mode = action.payload.mode;
        }
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateProjectMode.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { setProjectMetaData, setMetadataConversationId, setProjectMode, clearError, clearConversations, clearAttachments } = projectSlice.actions;

// Selectors with defensive checks
export const selectProjects = (state: { project: ProjectSliceState }) => state.project?.projects ?? [];
export const selectProjectMetaData = (state: { project: ProjectSliceState }) => state.project?.projectMetaData ?? null;
export const selectMetadataConversationId = (state: { project: ProjectSliceState }) => state.project?.metadataConversationId ?? null;
export const selectIsLoadingProjects = (state: { project: ProjectSliceState }) => state.project?.isLoading ?? false;
export const selectIsCreatingProject = (state: { project: ProjectSliceState }) => state.project?.isCreating ?? false;
export const selectIsDeletingProject = (state: { project: ProjectSliceState }) => state.project?.isDeleting ?? false;
export const selectIsUpdatingProject = (state: { project: ProjectSliceState }) => state.project?.isUpdating ?? false;
export const selectIsFetchingSingleProject = (state: { project: ProjectSliceState }) => state.project?.isFetchingSingle ?? false;
export const selectProjectError = (state: { project: ProjectSliceState }) => state.project?.error ?? null;
export const selectCurrentProject = (state: { project: ProjectSliceState }, currentProjectId: string | null) => 
  state.project?.projects?.find((p: ProjectData) => p.id === currentProjectId);
export const selectProjectConversations = (state: { project: ProjectSliceState }) => state.project?.conversations ?? [];
export const selectIsFetchingConversations = (state: { project: ProjectSliceState }) => state.project?.isFetchingConversations ?? false;
export const selectConversationsError = (state: { project: ProjectSliceState }) => state.project?.conversationsError ?? null;
export const selectProjectAttachments = (state: { project: ProjectSliceState }) => state.project?.attachments ?? [];
export const selectIsFetchingAttachments = (state: { project: ProjectSliceState }) => state.project?.isFetchingAttachments ?? false;
export const selectIsUploadingFiles = (state: { project: ProjectSliceState }) => state.project?.isUploadingFiles ?? false;
export const selectIsDeletingAttachment = (state: { project: ProjectSliceState }) => state.project?.isDeletingAttachment ?? false;
export const selectAttachmentsError = (state: { project: ProjectSliceState }) => state.project?.attachmentsError ?? null;

export default projectSlice.reducer;
